// src/components/Business/Scheduling/DraftPanel.jsx
// P2/Q2 — the governed clinical-draft surface. ADDITIVE: it drops into the EXISTING Scheduling
// appointment detail beside DocumentPanel, and creates no navigation item, no dashboard and no
// second workspace.
//
// THE BACKEND IS THE ONLY AUTHORITY. This panel decides nothing:
//   * which templates exist            -> the tenant's profile, via the server
//   * which sections a template has    -> the server's 12-section projection
//   * which are editable               -> server `editable` per section
//   * which buttons exist              -> server `allowed_actions`, never a role or a state
//                                         string inspected here
//   * who may approve                  -> the server's provider-identity authority
//
// It never reads a role, never compares a provider id, and never keeps a transition table. If the
// server offers an action the button appears; if it does not, it does not. That is the whole model,
// and it is the same one DocumentPanel beside it already follows.
//
// IT DOES NOT DISPLAY THE RAW TRANSCRIPT. The generator consumed governed N2 evidence, but this
// surface renders only what the projection returns per section — transcript REFERENCES in section 10
// and original-language evidence in section 11. Dumping the transcript here because it was available
// upstream would put clinical content on a screen no governed projection approved.

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import { FileText, Loader2, Sparkles, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Server action -> button label. A label table is NOT an authority: an action absent from
// allowed_actions is never rendered, whatever this map says.
const ACTION_LABELS = {
  submit_for_review: 'Send for provider review',
  reconcile: 'Mark reconciled',
  approve_final: 'Approve as final',
};

export default function DraftPanel({ appointmentId, businessId, api, t, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // A LOAD failure and an ACTION failure are different problems, exactly as in DocumentPanel: a load
  // failure means nothing can be shown, an action failure means the draft is still where it was and
  // blanking the section would remove the context the provider needs most.
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    if (!appointmentId || !businessId) return;
    setLoading(true);
    setLoadError(null);
    setActionError(null);
    try {
      const resp = await api.get(
        `${BACKEND_URL}/api/v1/scheduling/appointments/${encodeURIComponent(appointmentId)}` +
        `/drafts?business_id=${encodeURIComponent(businessId)}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setData(resp.data || null);
      setEdits({});
    } catch (e) {
      setLoadError(e.message || 'error');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, appointmentId, businessId]);

  useEffect(() => { load(); }, [load]);

  const generate = useCallback(async (templateId) => {
    setBusy(`generate:${templateId}`);
    setActionError(null);
    try {
      const resp = await api.post(
        `${BACKEND_URL}/api/v1/scheduling/appointments/${encodeURIComponent(appointmentId)}` +
        `/drafts/${encodeURIComponent(templateId)}/generate?business_id=${encodeURIComponent(businessId)}`,
        {});
      if (!resp.ok) throw new Error(resp.data?.detail?.reason || `HTTP ${resp.status}`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setActionError(e.message || 'error');
    } finally {
      setBusy(null);
    }
  }, [api, appointmentId, businessId, load, onChanged]);

  const saveSections = useCallback(async (templateId) => {
    setBusy(`save:${templateId}`);
    setActionError(null);
    try {
      const resp = await api.put(
        `${BACKEND_URL}/api/v1/scheduling/appointments/${encodeURIComponent(appointmentId)}` +
        `/drafts/${encodeURIComponent(templateId)}/sections?business_id=${encodeURIComponent(businessId)}`,
        { sections: edits[templateId] || {} });
      if (!resp.ok) throw new Error(resp.data?.detail?.reason || `HTTP ${resp.status}`);
      await load();
    } catch (e) {
      setActionError(e.message || 'error');
    } finally {
      setBusy(null);
    }
  }, [api, appointmentId, businessId, edits, load]);

  const act = useCallback(async (templateId, action) => {
    setBusy(`${action}:${templateId}`);
    setActionError(null);
    try {
      const resp = await api.post(
        `${BACKEND_URL}/api/v1/scheduling/appointments/${encodeURIComponent(appointmentId)}` +
        `/drafts/${encodeURIComponent(templateId)}/action?business_id=${encodeURIComponent(businessId)}`,
        { action });
      if (!resp.ok) throw new Error(resp.data?.detail?.reason || `HTTP ${resp.status}`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setActionError(e.message || 'error');
    } finally {
      setBusy(null);
    }
  }, [api, appointmentId, businessId, load, onChanged]);

  // Nothing to offer: the tenant's profile permits no governed templates. Rendering nothing is the
  // truthful outcome — an empty "documentation" heading would imply a capability this tenant lacks.
  if (!loading && !loadError && !(data?.templates || []).length) return null;

  const drafts = data?.drafts || [];
  const started = new Set(drafts.map((d) => d.template_id));

  return (
    <div className="pt-2 border-t border-gray-100" data-testid="draft-panel">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {t ? t('sched.draftsTitle') : 'Clinical documentation'}
        </span>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="draft-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {loadError && (
        <div className="text-sm text-red-600" data-testid="draft-load-error">
          Documentation could not be loaded.
        </div>
      )}

      {actionError && (
        <div className="text-sm text-red-600 mb-2" data-testid="draft-action-error">
          {actionError}
        </div>
      )}

      {!loading && !loadError && (
        <>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="draft-templates">
            {(data?.templates || []).map((tpl) => (
              <Button
                key={tpl.id}
                size="sm"
                variant="outline"
                disabled={started.has(tpl.id) || busy === `generate:${tpl.id}`}
                onClick={() => generate(tpl.id)}
                data-testid={`draft-generate-${tpl.id}`}
              >
                {busy === `generate:${tpl.id}`
                  ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  : <Sparkles className="h-3 w-3 mr-1" />}
                {tpl.label}
              </Button>
            ))}
          </div>

          {drafts.map((d) => (
            <div key={d.template_id} className="mb-4 rounded border border-gray-200 p-3"
                 data-testid={`draft-${d.template_id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{d.template_label}</span>
                <span className="text-xs rounded bg-gray-100 px-2 py-0.5"
                      data-testid={`draft-state-${d.template_id}`}>
                  {d.state}
                </span>
              </div>

              <div className="space-y-2">
                {(d.sections || []).map((s) => (
                  <div key={s.id} data-testid={`section-${s.id}`}>
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <span>{s.order}. {s.label}</span>
                      {s.applicability === 'not_applicable' && (
                        <span className="italic" data-testid={`section-na-${s.id}`}>
                          not applicable for this document
                        </span>
                      )}
                    </div>
                    {s.applicability !== 'not_applicable' && (
                      s.editable ? (
                        <textarea
                          className="w-full text-sm border rounded p-1 mt-0.5"
                          rows={2}
                          defaultValue={s.value || ''}
                          data-testid={`section-input-${s.id}`}
                          onChange={(e) => setEdits((prev) => ({
                            ...prev,
                            [d.template_id]: { ...(prev[d.template_id] || {}), [s.id]: e.target.value },
                          }))}
                        />
                      ) : (
                        <div className="text-sm text-gray-800 mt-0.5"
                             data-testid={`section-value-${s.id}`}>
                          {s.value || <span className="text-gray-400 italic">not generated</span>}
                        </div>
                      )
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mt-3">
                {(d.sections || []).some((s) => s.editable) && (
                  <Button size="sm" variant="outline"
                          disabled={busy === `save:${d.template_id}`}
                          onClick={() => saveSections(d.template_id)}
                          data-testid={`draft-save-${d.template_id}`}>
                    Save edits
                  </Button>
                )}
                {/* THE ONLY SOURCE OF ACTIONS. No role check, no state comparison, no local table. */}
                {(d.allowed_actions || []).map((a) => (
                  <Button key={a} size="sm"
                          disabled={busy === `${a}:${d.template_id}`}
                          onClick={() => act(d.template_id, a)}
                          data-testid={`draft-action-${a}-${d.template_id}`}>
                    {a === 'approve_final' && <ShieldCheck className="h-3 w-3 mr-1" />}
                    {ACTION_LABELS[a] || a}
                  </Button>
                ))}
              </div>

              {/* The NAME, never the internal staff id. The server resolves it through the same
                  staff-directory authority the rest of Scheduling uses and sends it as
                  `actor_display_name`; `actor_id` stays in the payload for traceability but is not
                  something a person should ever be shown. If the server sent no name, say who
                  approved it in the only other truthful way — not by falling back to the UUID. */}
              {d.approval && (
                <div className="text-xs text-gray-500 mt-2" data-testid={`draft-approved-${d.template_id}`}>
                  Approved by {d.approval.actor_display_name || 'a provider'} at {d.approval.at}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
